import { Ionicons } from '@expo/vector-icons';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';
import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, TouchableOpacity, RefreshControl, ActivityIndicator } from 'react-native';
import tw from 'twrnc';

import HeaderBar from '../../../components/HeaderBar';
import { DSGradientBackground } from '../../../design-system/components/DSGradientBackground';
import { DSText } from '../../../design-system/components/DSText';
import { useAppTheme } from '../../../design-system/ThemeProvider';
import { spacing, radius } from '../../../design-system/tokens';
import { t } from '../../../i18n/translations';
import { getUserStreakCalendar, getUserStreak, formatXP, type UserStreak, type StreakCalendarDay } from '../../../shared/services/gamificationService';
import { useLanguageStore } from '../../quiz-core/languageStore';
import { useUserProfile } from '../../quiz-core/useStore';

type ThemeColors = ReturnType<typeof useAppTheme>['colors'];
type IoniconName = React.ComponentProps<typeof Ionicons>['name'];
const gt = (key: string, language: string) => t(`gamification.${key}`, language);
const kingdom = {
  panel: '#080A18',
  panelSoft: '#101A32',
  gold: '#F6BE3A',
  ivory: '#FFF7DA',
  muted: 'rgba(255,248,232,0.74)',
  line: 'rgba(246,190,58,0.38)',
  rail: '#2A2440',
};

// Helper function to get streak icon based on streak length
const getStreakIcon = (streak: number): string => {
  if (streak >= 365) return 'trophy';
  if (streak >= 90) return 'diamond';
  if (streak >= 30) return 'flash';
  if (streak >= 7) return 'flame';
  return 'calendar';
};

// Helper function to get streak icon color
const getStreakIconColor = (streak: number, colors: ThemeColors): string => {
  if (streak >= 365) return colors.accent;
  if (streak >= 90) return colors.primaryLight;
  if (streak >= 30) return colors.accent;
  if (streak >= 7) return colors.incorrect;
  return colors.outline;
};

// Helper function to get activity icon
const getActivityIcon = (activityType?: string): string => {
  switch (activityType) {
    case 'quiz': return 'create';
    case 'daily_verse': return 'book';
    case 'social': return 'people';
    default: return 'checkmark';
  }
};

// Helper function to get streak tip based on streak length
const getStreakTip = (streak: number, language: string): string => {
  if (streak === 0) return gt('tip_start_streak', language);
  if (streak < 3) return gt('tip_daily_consistency', language);
  if (streak < 7) return gt('tip_grace_period', language);
  if (streak < 14) return gt('tip_multiple_activities', language);
  if (streak < 30) return gt('tip_set_reminders', language);
  return gt('tip_keep_going', language);
};

const formatDayLabel = (count: number, language: string): string => (
  count === 1 ? gt('day_singular', language) : gt('days', language)
);

const formatFriendlyDate = (dateValue: string | null | undefined, language: string): string => {
  if (!dateValue) return t('none', language);

  try {
    return new Intl.DateTimeFormat(language.replace('_', '-'), {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(dateValue));
  } catch {
    return new Date(dateValue).toLocaleDateString();
  }
};

const getMilestoneIconColor = (days: number, colors: ThemeColors): string => {
  if (days >= 365) return colors.accent;
  if (days >= 90) return colors.primaryLight;
  if (days >= 30) return colors.accent;
  return colors.incorrect;
};

interface CalendarDayProps {
  date: Date;
  currentMonth: Date;
  hasActivity: boolean;
  activityType?: string;
  isToday: boolean;
}

const CalendarDay: React.FC<CalendarDayProps> = ({ date, currentMonth, hasActivity, activityType, isToday }) => {
  const dayNumber = date.getDate();
  const isCurrentMonth = isSameMonth(date, currentMonth);

  return (
    <View
      style={tw`aspect-square p-1 items-center justify-center ${isCurrentMonth ? '' : 'opacity-30'}`}
    >
      <View
        style={[
          tw`w-10 h-10 rounded-full items-center justify-center`,
          isToday && { borderWidth: 2, borderColor: kingdom.gold },
          hasActivity && { backgroundColor: 'rgba(27,94,48,0.32)' },
        ]}
      >
        <Text style={[
          tw`text-sm font-medium`,
          { color: hasActivity ? '#A7F3D0' : kingdom.muted },
        ]}>
          {dayNumber}
        </Text>
        {hasActivity && (
          <View style={tw`absolute bottom-0`}>
            <Ionicons
              name={getActivityIcon(activityType) as IoniconName}
              size={10}
              color="#44D27A"
            />
          </View>
        )}
      </View>
    </View>
  );
};

export const StreakCalendarScreen: React.FC = () => {
  const { colors } = useAppTheme();
  const { id: userId, fetchProfile } = useUserProfile();
  const { language } = useLanguageStore();
  const g = (key: string) => gt(key, language);

  const [streakCalendar, setStreakCalendar] = useState<StreakCalendarDay[]>([]);
  const [streakInfo, setStreakInfo] = useState<UserStreak | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStreakData = useCallback(async (isRefresh = false) => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Load data in parallel for better performance
      const [calendarData, streak] = await Promise.all([
        getUserStreakCalendar(
          userId,
          currentMonth.getFullYear(),
          currentMonth.getMonth()
        ),
        getUserStreak(userId)
      ]);

      setStreakCalendar(calendarData);
      setStreakInfo(streak);

    } catch (error) {
      console.error('Error loading streak data:', error);
      setError(g('streak_calendar_error'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [userId, currentMonth, language]);

  const onRefresh = useCallback(() => {
    loadStreakData(true);
  }, [loadStreakData]);

  const changeMonth = useCallback((increment: number) => {
    setCurrentMonth(currentDate =>
      increment > 0 ? addMonths(currentDate, 1) : subMonths(currentDate, 1)
    );
  }, []);

  useEffect(() => {
    void fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (userId) {
      loadStreakData();
      setupRealtimeSubscription();
    }

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, [userId, loadStreakData, currentMonth]);

  // Real-time subscription
  const subscriptionRef = React.useRef<{ unsubscribe: () => void } | null>(null);

  const setupRealtimeSubscription = () => {
    if (!userId) return;

    // Clean up existing subscription
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
    }

    // Import and setup subscription
    import('../../../shared/services/gamificationService').then(({ subscribeToGamificationUpdates }) => {
      subscriptionRef.current = subscribeToGamificationUpdates(userId, {
        onStreakUpdate: (newStreak) => {
          setStreakInfo(newStreak);

          // Reload calendar data if streak changed significantly
          if (newStreak.current_streak !== streakInfo?.current_streak) {
            loadStreakData(true); // Silent reload
          }
        },
        onError: (error) => {
          console.error('Streak subscription error:', error);
          setError(g('streak_connection_error'));
        }
      });
    });
  };

  // Helper function to get calendar days for current month view
  const getCalendarDays = useCallback(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDate = monthStart;
    const endDate = monthEnd;

    // Get all days in the interval
    const daysInMonth = eachDayOfInterval({ start: startDate, end: endDate });

    // Create a 2D array for weeks
    const weeks: Date[][] = [];
    let week: Date[] = [];

    // Add empty days for the first week
    const firstDayOfMonth = getDay(monthStart);
    for (let i = 0; i < firstDayOfMonth; i++) {
      week.push(new Date(0)); // Empty day
    }

    // Add all days of the month
    daysInMonth.forEach(day => {
      if (week.length === 7) {
        weeks.push(week);
        week = [];
      }
      week.push(day);
    });

    // Add empty days for the last week
    while (week.length < 7) {
      week.push(new Date(0)); // Empty day
    }

    // Add the last week if it's not empty
    if (week.length > 0) {
      weeks.push(week);
    }

    return weeks;
  }, [currentMonth]);

  // Helper function to check if a day has activity
  const getDayActivity = useCallback((date: Date): { hasActivity: boolean, activityType?: string } => {
    if (!date || date.getTime() === 0) return { hasActivity: false };

    const formattedDate = format(date, 'yyyy-MM-dd');
    const dayData = streakCalendar.find(day => day.activity_date === formattedDate);

    return {
      hasActivity: dayData?.has_activity || false,
      activityType: dayData?.activity_type
    };
  }, [streakCalendar]);

  if (loading && !streakInfo) {
    return (
      <DSGradientBackground preset="surface">
        <HeaderBar title={g('streak_calendar')} variant="simple" showProfile={false} showClose={false} />
        <View style={tw`flex-1 justify-center items-center`}>
          <ActivityIndicator size="large" color={kingdom.gold} />
          <DSText variant="body" color={kingdom.muted} style={{ marginTop: spacing.lg }}>
            {t('loading', language)}...
          </DSText>
        </View>
      </DSGradientBackground>
    );
  }

  if (error && !streakInfo) {
    return (
      <DSGradientBackground preset="surface">
        <HeaderBar title={g('streak_calendar')} variant="simple" showProfile={false} showClose={false} />
        <View style={tw`flex-1 justify-center items-center p-4`}>
          <Ionicons name="alert-circle" size={32} color={colors.accent} style={{ marginBottom: spacing.sm }} />
          <DSText variant="body" color={kingdom.ivory} style={{ marginBottom: spacing.lg, textAlign: 'center' }}>
            {g('streak_calendar_error')}
          </DSText>
          <TouchableOpacity
            style={[
              tw`rounded-lg py-3 px-6`,
              { backgroundColor: '#3A2A08', borderRadius: radius.md, borderWidth: 1, borderColor: kingdom.line },
            ]}
            onPress={() => loadStreakData()}
          >
            <DSText variant="label" color={colors.onPrimary}>
              {t('retry', language)}
            </DSText>
          </TouchableOpacity>
        </View>
      </DSGradientBackground>
    );
  }

  // Get calendar weeks for the current month
  const calendarWeeks = getCalendarDays();
  const today = new Date();

  return (
    <DSGradientBackground preset="surface">
      <HeaderBar title={g('streak_calendar')} variant="simple" showProfile={false} showClose={false} />
      <ScrollView
        style={tw`flex-1`}
        contentContainerStyle={{ padding: spacing.lg, paddingBottom: spacing.xxxl + spacing.xl }}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={[kingdom.gold]}
            tintColor={kingdom.gold}
          />
        }
      >
        <View>
          {/* Streak Hero */}
          {streakInfo && (
            <View style={[
              tw`p-6 mb-6`,
              { backgroundColor: kingdom.panel, borderRadius: radius.lg, borderWidth: 1, borderColor: kingdom.line },
            ]}>
              <DSText variant="micro" uppercase color={kingdom.gold} style={{ marginBottom: spacing.sm, fontWeight: '700', letterSpacing: 1 }}>
                {g('streak_calendar')}
              </DSText>
              <View style={tw`flex-row items-center justify-between mb-4`}>
                <View style={tw`flex-1`}>
                  <DSText variant="title" color={kingdom.ivory}>
                    {g('current_streak')}
                  </DSText>
                  <View style={tw`flex-row items-baseline mt-1`}>
                    <DSText variant="display" color={kingdom.gold} style={{ marginRight: spacing.sm }}>
                      {streakInfo.current_streak}
                    </DSText>
                    <DSText variant="body" color={kingdom.ivory}>
                      {formatDayLabel(streakInfo.current_streak, language)}
                    </DSText>
                  </View>
                  <DSText variant="caption" color={kingdom.muted} style={{ marginTop: spacing.xs }}>
                    {getStreakTip(streakInfo.current_streak, language)}
                  </DSText>
                </View>

                <View style={[
                  tw`h-16 w-16 rounded-full items-center justify-center`,
                  { backgroundColor: 'rgba(246,190,58,0.12)', borderWidth: 2, borderColor: kingdom.line },
                ]}>
                  <Ionicons
                    name={getStreakIcon(streakInfo.current_streak) as IoniconName}
                    size={28}
                    color={getStreakIconColor(streakInfo.current_streak, colors)}
                  />
                </View>
              </View>

              <View style={tw`flex-row justify-between items-center`}>
                <View style={tw`rounded-2xl px-4 py-3`} >
                  <DSText variant="caption" color={kingdom.muted}>
                    {g('longest')}
                  </DSText>
                  <DSText variant="body" color={kingdom.ivory} style={{ fontWeight: '600' }}>
                    {streakInfo.longest_streak} {formatDayLabel(streakInfo.longest_streak, language)}
                  </DSText>
                </View>

                <View style={tw`rounded-2xl px-4 py-3`} >
                  <DSText variant="caption" color={kingdom.muted}>
                    {g('last_activity')}
                  </DSText>
                  <DSText variant="body" color={kingdom.ivory} style={{ fontWeight: '600' }}>
                    {formatFriendlyDate(streakInfo.last_activity_date, language)}
                  </DSText>
                </View>
              </View>
            </View>
          )}

          {!streakInfo && !loading && (
            <View
              style={[
                tw`p-6 mb-6`,
                { backgroundColor: kingdom.panel, borderRadius: radius.lg, borderWidth: 1, borderColor: kingdom.line },
              ]}
            >
              <DSText variant="micro" uppercase color={kingdom.gold} style={{ marginBottom: spacing.sm, fontWeight: '700', letterSpacing: 1 }}>
                {g('streak_calendar')}
              </DSText>
              <DSText variant="title" color={kingdom.ivory} style={{ marginBottom: spacing.sm }}>
                {g('start_your_streak')}
              </DSText>
              <DSText variant="body" color={kingdom.muted}>
                {g('tip_start_streak')}
              </DSText>
            </View>
          )}

          {/* Month Navigation */}
          <View style={[
            tw`flex-row items-center justify-between mb-4 p-4`,
            { backgroundColor: kingdom.panel, borderRadius: radius.lg, borderWidth: 1, borderColor: kingdom.line },
          ]}>
            <TouchableOpacity
              style={tw`p-2`}
              onPress={() => changeMonth(-1)}
            >
              <View style={tw`flex-row items-center`}>
                <Ionicons name="chevron-back" size={16} color={kingdom.gold} />
                <DSText variant="label" color={kingdom.gold} style={{ marginLeft: spacing.xs }}>
                  {t('previous', language)}
                </DSText>
              </View>
            </TouchableOpacity>

            <DSText variant="title" color={kingdom.ivory}>
              {format(currentMonth, 'MMMM yyyy')}
            </DSText>

            <TouchableOpacity
              style={tw`p-2`}
              onPress={() => changeMonth(1)}
            >
              <View style={tw`flex-row items-center`}>
                <DSText variant="label" color={kingdom.gold} style={{ marginRight: spacing.xs }}>
                  {t('next', language)}
                </DSText>
                <Ionicons name="chevron-forward" size={16} color={kingdom.gold} />
              </View>
            </TouchableOpacity>
          </View>

          {/* Calendar */}
          <View style={[
            tw`p-4 mb-6`,
            { backgroundColor: kingdom.panel, borderRadius: radius.lg, borderWidth: 1, borderColor: kingdom.line },
          ]}>
            <DSText variant="micro" uppercase color={kingdom.gold} style={{ marginBottom: spacing.md, fontWeight: '700', letterSpacing: 1 }}>
              {format(currentMonth, 'MMMM yyyy')}
            </DSText>
            {/* Day headers */}
            <View style={tw`flex-row mb-2`}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <View key={day} style={tw`flex-1 items-center`}>
                  <DSText variant="micro" color={colors.outline} uppercase>
                    {t(day.toLowerCase(), language)}
                  </DSText>
                </View>
              ))}
            </View>

            {/* Calendar grid */}
            {calendarWeeks.map((week, weekIndex) => (
              <View key={`week-${weekIndex}`} style={tw`flex-row`}>
                {week.map((day, dayIndex) => {
                  const { hasActivity, activityType } = getDayActivity(day);
                  const isToday = day.getTime() !== 0 && isSameDay(day, today);

                  return (
                    <View key={`day-${weekIndex}-${dayIndex}`} style={tw`flex-1`}>
                      {day.getTime() !== 0 && (
                        <CalendarDay
                          date={day}
                          currentMonth={currentMonth}
                          hasActivity={hasActivity}
                          activityType={activityType}
                          isToday={isToday}
                        />
                      )}
                    </View>
                  );
                })}
              </View>
            ))}

            {/* Legend */}
            <View style={[
              tw`mt-4 pt-4`,
              { borderTopWidth: 1, borderTopColor: kingdom.line },
            ]}>
              <DSText variant="caption" color={kingdom.muted} style={{ fontWeight: '500', marginBottom: spacing.sm }}>
                {g('activity_types')}
              </DSText>
              <View style={tw`flex-row flex-wrap`}>
                <View style={tw`flex-row items-center mr-4 mb-2`}>
                  <Ionicons name="create" size={14} color={colors.correct} style={{ marginRight: spacing.xs }} />
                        <DSText variant="micro" color={kingdom.muted}>{t('quiz', language)}</DSText>
                </View>
                <View style={tw`flex-row items-center mr-4 mb-2`}>
                  <Ionicons name="book" size={14} color={colors.correct} style={{ marginRight: spacing.xs }} />
                  <DSText variant="micro" color={kingdom.muted}>{g('daily_verse')}</DSText>
                </View>
                <View style={tw`flex-row items-center mr-4 mb-2`}>
                  <Ionicons name="people" size={14} color={colors.correct} style={{ marginRight: spacing.xs }} />
                  <DSText variant="micro" color={kingdom.muted}>{t('social', language)}</DSText>
                </View>
              </View>
            </View>
          </View>

          {/* Streak Milestones */}
          <View style={[
            tw`p-6 mb-4`,
            { backgroundColor: kingdom.panel, borderRadius: radius.lg, borderWidth: 1, borderColor: kingdom.line },
          ]}>
            <DSText variant="title" color={kingdom.ivory} style={{ marginBottom: spacing.lg }}>
              {g('streak_milestones')}
            </DSText>

            <View style={tw`space-y-4`}>
              {[
                { days: 7, icon: 'flame' as const, name: g('milestone_week'), xp: 30 },
                { days: 30, icon: 'flash' as const, name: g('milestone_month'), xp: 100 },
                { days: 90, icon: 'diamond' as const, name: g('milestone_quarter'), xp: 250 },
                { days: 365, icon: 'trophy' as const, name: g('milestone_year'), xp: 1000 }
              ].map((milestone) => {
                const isCompleted = streakInfo && streakInfo.longest_streak >= milestone.days;
                const isNext = streakInfo &&
                  streakInfo.longest_streak < milestone.days &&
                  (!streakInfo.current_streak || streakInfo.current_streak < milestone.days);
                const progress = streakInfo ?
                  Math.min(100, (streakInfo.current_streak / milestone.days) * 100) : 0;

                const bgColor = isCompleted ? 'rgba(27,94,48,0.28)' : isNext ? 'rgba(246,190,58,0.12)' : kingdom.panelSoft;
                const borderColor = isCompleted ? 'rgba(68,210,122,0.48)' : kingdom.line;
                const textColor = kingdom.ivory;
                const subTextColor = isCompleted ? '#A7F3D0' : kingdom.muted;

                return (
                  <View
                    key={milestone.days}
                    style={[
                      tw`p-3`,
                      { backgroundColor: bgColor, borderRadius: radius.md, borderWidth: 1, borderColor },
                    ]}
                  >
                    <View style={tw`flex-row items-center`}>
                      <Ionicons
                        name={milestone.icon as IoniconName}
                        size={24}
                        color={getMilestoneIconColor(milestone.days, colors)}
                        style={{ marginRight: spacing.md }}
                      />
                      <View style={tw`flex-1`}>
                        <DSText variant="label" color={textColor}>
                          {milestone.name}
                        </DSText>
                        <DSText variant="micro" color={subTextColor}>
                          {milestone.days} {g('days')} • +{formatXP(milestone.xp)} {g('xp_short')}
                        </DSText>
                      </View>
                      {isCompleted && (
                        <View style={[
                          tw`rounded-full w-6 h-6 items-center justify-center`,
                          { backgroundColor: colors.correct },
                        ]}>
                          <Ionicons name="checkmark" size={14} color={colors.white} />
                        </View>
                      )}
                    </View>

                    {!isCompleted && (
                      <View style={{ marginTop: spacing.sm }}>
                        <View style={[
                          tw`rounded-full h-2`,
                          { backgroundColor: kingdom.rail },
                        ]}>
                          <View
                            style={[
                              tw`rounded-full h-2`,
                              {
                                backgroundColor: isNext ? kingdom.gold : 'rgba(255,248,232,0.28)',
                                width: `${progress}%`,
                              },
                            ]}
                          />
                        </View>
                        <DSText variant="micro" color={kingdom.muted} style={{ marginTop: spacing.xs, textAlign: 'right' }}>
                          {streakInfo?.current_streak || 0}/{milestone.days} {g('days')}
                        </DSText>
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          </View>

          {/* Streak Tips */}
          <View style={[
            tw`p-6`,
            { backgroundColor: kingdom.panel, borderRadius: radius.lg, borderWidth: 1, borderColor: kingdom.line },
          ]}>
            <DSText variant="title" color={kingdom.ivory} style={{ marginBottom: spacing.lg }}>
              {g('streak_tips')}
            </DSText>

            <View style={tw`space-y-3`}>
              {[
                { id: 1, tip: g('tip_daily_consistency') },
                { id: 2, tip: g('tip_grace_period') },
                { id: 3, tip: g('tip_multiple_activities') },
                { id: 4, tip: g('tip_set_reminders') }
              ].map((tip) => (
                <View key={tip.id} style={tw`flex-row items-start`}>
                  <Ionicons name="bulb" size={18} color={colors.accent} style={{ marginRight: spacing.sm, marginTop: 2 }} />
                  <DSText variant="body" color={kingdom.muted} style={{ flex: 1 }}>
                    {tip.tip}
                  </DSText>
                </View>
              ))}
            </View>

            {streakInfo?.current_streak === 0 && (
              <TouchableOpacity
                style={[
                  tw`py-3 px-4 mt-4`,
                  { backgroundColor: '#3A2A08', borderRadius: radius.md, borderWidth: 1, borderColor: kingdom.line },
                ]}
                onPress={() => {}}
              >
                <DSText variant="label" color={colors.onPrimary} align="center">
                  {g('start_your_streak')}
                </DSText>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </ScrollView>
    </DSGradientBackground>
  );
};
